import { CrmList } from '@/components/CrmList';
import { BusinessModal } from '@/modules/zy-crm/components/business-modal';
import './styles.css';
import { useAccess } from '@umijs/max';
import { useSession } from '@zhiyue/core';
import {
  Alert,
  Button,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Empty from '@/components/QuietEmpty';
import {
  type TripKind,
  tripRequest,
  tripRows,
} from '@/services/bff/business-trip.api';
import type {
  BusinessTripCrmRelation,
  TripActivity,
  TripLog,
  TripMember,
  TripRecord,
} from '@/services/bff/types/business-trip';
import {
  COLLABORATION_CHANGED,
  notifyCollaborationChanged,
} from '@/services/collaborationEvents';

export const tripError = (error: unknown) => {
  const status =
    (error as { response?: { status?: number }; status?: number })?.response
      ?.status || (error as { status?: number })?.status;
  return status === 403
    ? '你没有查看或操作此出差数据的权限。'
    : status === 401
      ? '认证已失效，请刷新会话后重试。'
      : status === 404
        ? '数据不存在或已删除。'
        : status === 400 || status === 409 || status === 422
          ? '操作未通过校验，请检查字段及当前业务状态。'
          : '出差助手暂时不可用，请重试。';
};
const statuses = {
  closed: '已关闭',
  planning: '待编排',
  included: '已纳入计划',
  completed: '已完成',
  canceled: '已取消',
  assessable: '待评估',
  deferred: '已延期',
  draft: '草稿',
  in_progress: '进行中',
  pending: '未排期',
  scheduled: '待执行',
};
export const tripStatus = (status?: string | null) =>
  statuses[status as keyof typeof statuses] || status || '—';
const options = (values: string[]) =>
  values.map((value) => ({ value, label: tripStatus(value) }));
const slots = [
  { value: 'morning', label: '上午' },
  { value: 'afternoon', label: '下午' },
  { value: 'evening', label: '晚上' },
  { value: 'all_day', label: '全天' },
  { value: 'custom', label: '自定义' },
];
const objectTypes = [
  { value: 'lead', label: '线索' },
  { value: 'customer', label: '客户' },
  { value: 'contact', label: '联系人' },
  { value: 'opportunity', label: '商机' },
];
const id = encodeURIComponent;
export const tripAllowed = (access: Record<string, boolean>, action: string) =>
  Boolean(access['business-trips'] && access[`business-trips:${action}`]);

function CrmReferenceSelect({
  value,
  onChange,
  objectType,
}: {
  value?: string;
  onChange?: (value: string) => void;
  objectType?: string;
}) {
  const [rows, setRows] = useState<Array<{ value: string; label: string }>>([]);
  const [error, setError] = useState('');
  const generation = useRef(0);
  useEffect(() => {
    generation.current++;
    setRows([]);
    setError('');
  }, [objectType]);
  const search = async (keyword: string) => {
    if (!objectType) return;
    const version = ++generation.current;
    try {
      const result = await tripRequest<{
        list: Array<{ objectUuid: string; displayName: string }>;
      }>('crm/references', 'GET', undefined, {
        type: objectType,
        keyword,
        page: 1,
        pageSize: 20,
      });
      if (version === generation.current) {
        setRows(
          result.list.map((row) => ({
            value: row.objectUuid,
            label: row.displayName || row.objectUuid,
          })),
        );
        setError('');
      }
    } catch (err) {
      if (version === generation.current) setError(tripError(err));
    }
  };
  return (
    <>
      <Select
        disabled={!objectType}
        showSearch
        filterOption={false}
        value={value}
        onChange={onChange}
        onSearch={search}
        onOpenChange={(open) => {
          if (open) void search('');
        }}
        options={rows}
        placeholder="搜索关联对象"
        style={{ minWidth: 200 }}
      />
      {error && <Typography.Text type="danger">{error}</Typography.Text>}
    </>
  );
}
function RelationField({ name }: { name: number }) {
  const form = Form.useFormInstance();
  const type = Form.useWatch(['crmRelations', name, 'objectType']);
  return (
    <Space wrap>
      <Form.Item name={[name, 'objectType']} rules={[{ required: true }]}>
        <Select
          options={objectTypes}
          onChange={() =>
            form.setFieldValue(['crmRelations', name, 'objectUuid'], undefined)
          }
          placeholder="对象类型"
          style={{ width: 120 }}
        />
      </Form.Item>
      <Form.Item name={[name, 'objectUuid']} rules={[{ required: true }]}>
        <CrmReferenceSelect objectType={type} />
      </Form.Item>
    </Space>
  );
}

export function TripEditor({
  kind,
  initial,
  relation,
  onClose,
  onSaved,
}: {
  kind: TripKind;
  initial?: TripRecord;
  relation?: BusinessTripCrmRelation;
  onClose: () => void;
  onSaved: (value: TripRecord) => void;
}) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const plan = kind === 'plans';
  const submit = async () => {
    const values = await form.validateFields();
    const payload = { ...values };
    for (const field of ['startDate', 'endDate', 'deadline'])
      if (values[field]) payload[field] = values[field].format('YYYY-MM-DD');
    // Do not erase relations omitted by provider detail responses.
    if (
      initial &&
      !initial.crmRelations &&
      !form.isFieldsTouched(['crmRelations'])
    )
      delete payload.crmRelations;
    if (payload.crmRelations)
      payload.crmRelations = payload.crmRelations.map(
        (row: BusinessTripCrmRelation) => ({
          objectType: row.objectType,
          objectUuid: row.objectUuid,
        }),
      );
    setSaving(true);
    setError('');
    try {
      const result = await tripRequest<TripRecord>(
        initial ? `${kind}/${id(initial.uuid)}` : kind,
        initial ? 'PATCH' : 'POST',
        payload,
      );
      notifyCollaborationChanged();
      onSaved(result);
      onClose();
    } catch (err) {
      setError(tripError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      closable={{ 'aria-label': '关闭' }}
      keyboard
      rootClassName="crm-business-trip-surface"
      open
      width={760}
      title={`${initial ? '编辑' : '新建'}出差${plan ? '计划' : '事项'}`}
      onCancel={onClose}
      onOk={() => {
        void submit().catch(() => undefined);
      }}
      confirmLoading={saving}
      destroyOnHidden
    >
      {error && <Alert type="error" showIcon title={error} />}
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          priority: 'medium',
          ...initial,
          startDate: initial?.startDate ? dayjs(initial.startDate) : undefined,
          endDate: initial?.endDate ? dayjs(initial.endDate) : undefined,
          deadline: initial?.deadline ? dayjs(initial.deadline) : undefined,
          crmRelations: initial?.crmRelations || (relation ? [relation] : []),
          dateChangeScheduleAction: 'shift',
        }}
      >
        <Form.Item
          name="title"
          label="标题"
          rules={[{ required: true }, { max: 200 }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="destinationCity"
          label="目的城市"
          rules={[{ required: !plan }, { max: 100 }]}
        >
          <Input />
        </Form.Item>
        {plan ? (
          <>
            <Form.Item name="nearbyCities" label="周边城市">
              <Select mode="tags" />
            </Form.Item>
            <Space wrap>
              <Form.Item
                name="startDate"
                label="开始日期"
                rules={[{ required: true }]}
              >
                <DatePicker />
              </Form.Item>
              <Form.Item
                name="endDate"
                label="结束日期"
                dependencies={['startDate']}
                rules={[
                  { required: true },
                  ({ getFieldValue }) => ({
                    validator: (_, value) =>
                      !value ||
                      !getFieldValue('startDate') ||
                      !value.isBefore(getFieldValue('startDate'), 'day')
                        ? Promise.resolve()
                        : Promise.reject(new Error('结束日期不能早于开始日期')),
                  }),
                ]}
              >
                <DatePicker />
              </Form.Item>
            </Space>
            {initial && (
              <Form.Item
                name="dateChangeScheduleAction"
                label="修改日期时的行程处理"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { value: 'shift', label: '整体平移已排期活动' },
                    { value: 'clear', label: '清除已有排期，重新安排' },
                  ]}
                />
              </Form.Item>
            )}
            <Form.Item name="tripObjective" label="出差目标">
              <Input.TextArea />
            </Form.Item>
            <Form.Item name="mustWinResult" label="必须取得的成果">
              <Input.TextArea />
            </Form.Item>
          </>
        ) : (
          <>
            <Form.Item name="description" label="说明">
              <Input.TextArea />
            </Form.Item>
            <Space wrap>
              <Form.Item name="province" label="省份">
                <Input />
              </Form.Item>
              <Form.Item name="businessType" label="业务类型">
                <Input />
              </Form.Item>
              <Form.Item name="priority" label="优先级">
                <Select
                  style={{ width: 100 }}
                  options={[
                    { value: 'low', label: '低' },
                    { value: 'medium', label: '中' },
                    { value: 'high', label: '高' },
                  ]}
                />
              </Form.Item>
            </Space>
            <Space wrap>
              <Form.Item name="estimatedHours" label="预计时长（小时）">
                <InputNumber min={0.25} step={0.25} />
              </Form.Item>
              <Form.Item name="deadline" label="截止日期">
                <DatePicker />
              </Form.Item>
            </Space>
            <Form.Item name="businessGoal" label="业务目标">
              <Input.TextArea />
            </Form.Item>
            <Form.Item name="expectedOutcome" label="预期成果">
              <Input.TextArea />
            </Form.Item>
            <Form.Item name="requiredVisitObject" label="需拜访对象">
              <Input />
            </Form.Item>
            <Form.Item name="timeFlexibility" label="时间灵活度">
              <Select
                allowClear
                options={[
                  { value: 'fixed_window', label: '固定窗口' },
                  { value: 'near_term', label: '近期安排' },
                  { value: 'deferrable', label: '可以延期' },
                ]}
              />
            </Form.Item>
            <Form.List name="crmRelations">
              {(fields, { add, remove }) => (
                <>
                  <Typography.Paragraph>关联 CRM 对象</Typography.Paragraph>
                  {fields.map((field) => (
                    <div key={field.key}>
                      <RelationField name={field.name} />
                      <Button onClick={() => remove(field.name)}>
                        移除关联
                      </Button>
                    </div>
                  ))}
                  <Button disabled={fields.length >= 20} onClick={() => add()}>
                    添加关联对象
                  </Button>
                </>
              )}
            </Form.List>
          </>
        )}
        <Form.Item name="remark" label="备注">
          <Input.TextArea />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function MemberManager({
  path,
  plan,
  members,
  canManage,
  onChanged,
}: {
  path: string;
  plan?: boolean;
  members: TripMember[];
  canManage: boolean;
  onChanged: () => void;
}) {
  const [directory, setDirectory] = useState<TripMember[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [permission, setPermission] = useState<'view' | 'edit'>('view');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const search = async (keyword: string) => {
    try {
      const rows = await tripRequest<TripMember[] | { data: TripMember[] }>(
        'members',
        'GET',
        undefined,
        { keyword },
      );
      setDirectory(tripRows(rows));
      setError('');
    } catch (err) {
      setError(tripError(err));
    }
  };
  const change = async (
    method: 'POST' | 'PATCH' | 'DELETE',
    suffix: string,
    body?: unknown,
  ) => {
    setBusy(true);
    try {
      await tripRequest(`${path}/members${suffix}`, method, body);
      setError('');
      setSelected([]);
      notifyCollaborationChanged();
      onChanged();
    } catch (err) {
      setError(tripError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Space orientation="vertical" style={{ width: '100%' }}>
      {error && <Alert type="error" title={error} />}
      {canManage && (
        <Space wrap>
          <Select
            mode="multiple"
            showSearch
            filterOption={false}
            onSearch={(text) => void search(text)}
            onOpenChange={(open) => {
              if (open) void search('');
            }}
            value={selected}
            onChange={setSelected}
            style={{ minWidth: 220 }}
            placeholder="搜索并选择成员"
            options={directory.map((row) => ({
              value: row.memberUuid,
              label: row.name || row.memberUuid,
            }))}
          />
          {!plan && (
            <Select
              value={permission}
              onChange={setPermission}
              options={[
                { value: 'view', label: '查看' },
                { value: 'edit', label: '编辑' },
              ]}
            />
          )}
          <Button
            loading={busy}
            disabled={!selected.length}
            onClick={() =>
              void change('POST', '', {
                memberUuids: selected,
                ...(!plan ? { permission } : {}),
              })
            }
          >
            添加成员
          </Button>
        </Space>
      )}
      <Table
        rowKey="memberUuid"
        size="small"
        dataSource={members}
        pagination={false}
        columns={[
          { title: '成员', render: (_, row) => row.name || row.memberUuid },
          {
            title: '权限',
            render: (_, row) =>
              row.isOwner ? (
                '所有者'
              ) : canManage ? (
                <Select
                  disabled={busy}
                  value={
                    plan
                      ? row.canEdit
                        ? 'edit'
                        : 'view'
                      : row.permission || 'view'
                  }
                  options={[
                    { value: 'view', label: '查看' },
                    { value: 'edit', label: '编辑' },
                  ]}
                  onChange={(value) =>
                    void change(
                      'PATCH',
                      `/${id(row.memberUuid)}`,
                      plan
                        ? { canEdit: value === 'edit' }
                        : { permission: value },
                    )
                  }
                />
              ) : (plan ? row.canEdit : row.permission === 'edit') ? (
                '编辑'
              ) : (
                '查看'
              ),
          },
          {
            title: '操作',
            render: (_, row) =>
              canManage &&
              !row.isOwner && (
                <Popconfirm
                  title="移除此成员的协作权限？"
                  onConfirm={() => change('DELETE', `/${id(row.memberUuid)}`)}
                >
                  <Button danger disabled={busy}>
                    移除
                  </Button>
                </Popconfirm>
              ),
          },
        ]}
      />
    </Space>
  );
}

function ScheduleEditor({
  activity,
  planUuid,
  onClose,
  onSaved,
}: {
  activity: TripActivity;
  planUuid: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form] = Form.useForm();
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState<TripMember[]>([]);
  const startSlot = Form.useWatch('executionStartTimeSlot', form);
  const endSlot = Form.useWatch('executionEndTimeSlot', form);
  useEffect(() => {
    void tripRequest<TripRecord>(`plans/${id(planUuid)}`)
      .then((plan) => setMembers(plan.participants || []))
      .catch((err) => setError(tripError(err)));
  }, [planUuid]);
  const save = async () => {
    const values = await form.validateFields();
    for (const field of ['executionStartDate', 'executionEndDate'])
      if (values[field]) values[field] = values[field].format('YYYY-MM-DD');
    setSaving(true);
    try {
      await tripRequest(
        `plans/${id(planUuid)}/items/${id(activity.businessTripItemUuid)}`,
        'PATCH',
        values,
      );
      notifyCollaborationChanged();
      onSaved();
      onClose();
    } catch (err) {
      setError(tripError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      closable={{ 'aria-label': '关闭' }}
      keyboard
      rootClassName="crm-business-trip-surface"
      open
      title="安排出差活动"
      onCancel={onClose}
      onOk={() => {
        void save().catch(() => undefined);
      }}
      confirmLoading={saving}
    >
      {error && <Alert type="error" title={error} />}
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          ...activity,
          executionStartDate:
            activity.executionStartDate || activity.executionDate
              ? dayjs(activity.executionStartDate || activity.executionDate)
              : undefined,
          executionEndDate:
            activity.executionEndDate || activity.executionDate
              ? dayjs(activity.executionEndDate || activity.executionDate)
              : undefined,
        }}
      >
        <Form.Item name="assigneeMemberUuid" label="负责人">
          <Select
            allowClear
            options={members.map((row) => ({
              value: row.memberUuid,
              label: row.name || row.memberUuid,
            }))}
          />
        </Form.Item>
        <Form.Item
          name="executionStartDate"
          label="开始日期"
          rules={[{ required: true }]}
        >
          <DatePicker />
        </Form.Item>
        <Form.Item
          name="executionEndDate"
          label="结束日期"
          rules={[{ required: true }]}
        >
          <DatePicker />
        </Form.Item>
        <Form.Item
          name="executionStartTimeSlot"
          label="开始时段"
          rules={[{ required: true }]}
        >
          <Select options={slots} />
        </Form.Item>
        {startSlot === 'custom' && (
          <Form.Item
            name="executionStartCustomTimeSlot"
            label="自定义开始时间"
            rules={[
              { required: true },
              { pattern: /^([01]\d|2[0-3]):[0-5]\d$/, message: '请输入 HH:mm' },
            ]}
          >
            <Input placeholder="09:30" />
          </Form.Item>
        )}
        <Form.Item
          name="executionEndTimeSlot"
          label="结束时段"
          rules={[{ required: true }]}
        >
          <Select options={slots} />
        </Form.Item>
        {endSlot === 'custom' && (
          <Form.Item
            name="executionEndCustomTimeSlot"
            label="自定义结束时间"
            rules={[
              { required: true },
              { pattern: /^([01]\d|2[0-3]):[0-5]\d$/, message: '请输入 HH:mm' },
            ]}
          >
            <Input placeholder="11:30" />
          </Form.Item>
        )}
        <Form.Item name="executionStatus" label="执行状态">
          <Select options={options(['pending', 'scheduled', 'completed'])} />
        </Form.Item>
        <Form.Item name="locationNote" label="地点">
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export function TripDetail({
  kind,
  uuid,
  onClose,
}: {
  kind: TripKind | 'activities';
  uuid: string;
  onClose: () => void;
}) {
  const access = useAccess() as Record<string, boolean>;
  const { currentUser } = useSession();
  const [record, setRecord] = useState<TripRecord>();
  const [activity, setActivity] = useState<TripActivity>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [scheduling, setScheduling] = useState<TripActivity>();
  const [members, setMembers] = useState<TripMember[]>([]);
  const [logs, setLogs] = useState<TripLog[]>([]);
  const [logCursor, setLogCursor] = useState<string>();
  const [recycled, setRecycled] = useState<TripActivity[]>([]);
  const [candidates, setCandidates] = useState<TripRecord[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('detail');
  const [completion, setCompletion] = useState<Record<string, string>>();
  const [removing, setRemoving] = useState<TripActivity>();
  const [reason, setReason] = useState('itinerary_changed');
  const [reasonDetail, setReasonDetail] = useState('');
  const [notes, setNotes] = useState<Record<string, string>>({});
  const version = useRef(0);
  const path = `${kind}/${id(uuid)}`;
  const plan = kind === 'plans';
  const load = useCallback(async () => {
    const generation = ++version.current;
    setLoading(true);
    setError('');
    try {
      const value = await tripRequest<TripRecord & TripActivity>(path);
      if (generation !== version.current) return;
      setRecord(kind === 'activities' ? value.item : value);
      setActivity(kind === 'activities' ? value : undefined);
      setMembers(
        value.participants?.map((member) => ({
          ...member,
          isOwner: member.memberUuid === value.creatorMemberUuid,
        })) ||
          value.members ||
          [],
      );
      if (plan)
        setNotes(
          Object.fromEntries(
            (value.days || []).map((day) => [day.executionDate, day.dailyNote]),
          ),
        );
    } catch (err) {
      if (generation === version.current) {
        setRecord(undefined);
        setError(tripError(err));
      }
    } finally {
      if (generation === version.current) setLoading(false);
    }
  }, [
    path,
    kind,
    currentUser?.tenantUuid,
    (currentUser as API.CurrentUser | undefined)?.organizationUuid,
    currentUser?.memberUuid,
  ]);
  useEffect(() => {
    setRecord(undefined);
    setTab('detail');
    void load();
    const refresh = () => void load();
    window.addEventListener(COLLABORATION_CHANGED, refresh);
    return () => {
      version.current++;
      window.removeEventListener(COLLABORATION_CHANGED, refresh);
    };
  }, [load]);
  const mutate = async (
    suffix: string,
    method: 'POST' | 'PATCH' | 'DELETE',
    body?: unknown,
  ) => {
    setBusy(true);
    try {
      await tripRequest(path + suffix, method, body);
      notifyCollaborationChanged();
      setError('');
      return true;
    } catch (err) {
      setError(tripError(err));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const loadTab = async (key: string, cursor?: string) => {
    setTab(key);
    setError('');
    try {
      if (key === 'members' && !plan)
        setMembers(
          tripRows(
            await tripRequest<TripMember[] | { data: TripMember[] }>(
              `${path}/members`,
            ),
          ),
        );
      if (key === 'logs') {
        const result = await tripRequest<
          TripLog[] | { data: TripLog[]; nextCursor?: string }
        >(
          `${path}/activities`,
          'GET',
          undefined,
          cursor ? { cursor } : undefined,
        );
        setLogs((previous) =>
          cursor ? [...previous, ...tripRows(result)] : tripRows(result),
        );
        setLogCursor(Array.isArray(result) ? undefined : result.nextCursor);
      }
      if (key === 'recycle')
        setRecycled(
          tripRows(
            await tripRequest<TripActivity[] | { data: TripActivity[] }>(
              `${path}/recycle-bin`,
            ),
          ),
        );
      if (key === 'candidates')
        setCandidates(
          tripRows(
            await tripRequest<TripRecord[] | { data: TripRecord[] }>(
              `${path}/candidates`,
            ),
          ),
        );
    } catch (err) {
      setError(tripError(err));
    }
  };
  const canEdit =
    tripAllowed(access, 'edit') &&
    Boolean(activity ? activity.canEdit : record?.canEdit);
  const canManage =
    tripAllowed(access, 'manage-members') &&
    Boolean(activity ? activity.canManageMembers : record?.canManageMembers);
  const canDelete =
    tripAllowed(access, 'delete') && Boolean(record?.canManageMembers);
  const activityTable = (rows: TripActivity[], trash = false) => (
    <Table<TripActivity>
      rowKey={(row) => row.activityUuid || row.uuid}
      size="small"
      scroll={{ x: 780 }}
      dataSource={rows}
      pagination={false}
      columns={[
        {
          title: '事项',
          render: (_, row) => row.item?.title || row.businessTripItemUuid,
        },
        {
          title: '日期 / 时段',
          render: (_, row) =>
            `${row.executionStartDate || row.executionDate || '未排期'} ${slots.find((slot) => slot.value === row.executionStartTimeSlot)?.label || ''} ${row.executionStartCustomTimeSlot || ''} → ${row.executionEndDate || ''} ${slots.find((slot) => slot.value === row.executionEndTimeSlot)?.label || ''} ${row.executionEndCustomTimeSlot || ''}`,
        },
        { title: '状态', render: (_, row) => tripStatus(row.executionStatus) },
        {
          title: '操作',
          render: (_, row, index) => (
            <Space wrap>
              {trash ? (
                canEdit && (
                  <Button
                    onClick={async () => {
                      if (
                        await mutate(
                          `/items/${id(row.businessTripItemUuid)}/restore`,
                          'PATCH',
                        )
                      )
                        void loadTab('recycle');
                    }}
                  >
                    恢复
                  </Button>
                )
              ) : (
                <>
                  {(canEdit ||
                    (tripAllowed(access, 'edit') && row.canEdit)) && (
                    <Button onClick={() => setScheduling(row)}>编辑排期</Button>
                  )}
                  {canEdit && (
                    <>
                      <Button
                        disabled={index === 0 || !row.executionDate}
                        onClick={() => {
                          const dayRows = rows.filter(
                            (item) => item.executionDate === row.executionDate,
                          );
                          const pos = dayRows.findIndex(
                            (item) => item.uuid === row.uuid,
                          );
                          if (pos < 1) return;
                          [dayRows[pos - 1], dayRows[pos]] = [
                            dayRows[pos],
                            dayRows[pos - 1],
                          ];
                          void mutate('/items/reorder', 'PATCH', {
                            executionDate: row.executionDate,
                            businessTripItemUuids: dayRows.map(
                              (item) => item.businessTripItemUuid,
                            ),
                          });
                        }}
                      >
                        上移
                      </Button>
                      <Button onClick={() => setRemoving(row)}>移出</Button>
                      <Button
                        onClick={() =>
                          void mutate(
                            `/items/${id(row.businessTripItemUuid)}`,
                            'PATCH',
                            { clearSchedule: true },
                          )
                        }
                      >
                        清除排期
                      </Button>
                    </>
                  )}
                  {canDelete && (
                    <Popconfirm
                      title="移入回收站？此活动将从日历及行程中隐藏。"
                      onConfirm={async () => {
                        await mutate(
                          `/items/${id(row.businessTripItemUuid)}/trash`,
                          'PATCH',
                        );
                      }}
                    >
                      <Button danger>回收</Button>
                    </Popconfirm>
                  )}
                </>
              )}
            </Space>
          ),
        },
      ]}
    />
  );
  const tabs = [
    {
      key: 'detail',
      label: '详情',
      children: record ? (
        <Descriptions
          column={1}
          items={[
            { key: 'title', label: '标题', children: record.title },
            { key: 'city', label: '目的地', children: record.destinationCity },
            {
              key: 'status',
              label: '状态',
              children: tripStatus(record.status),
            },
            {
              key: 'goal',
              label: '目标',
              children: record.tripObjective || record.businessGoal,
            },
            {
              key: 'result',
              label: '预期成果',
              children: record.mustWinResult || record.expectedOutcome,
            },
            { key: 'description', label: '说明', children: record.description },
            { key: 'remark', label: '备注', children: record.remark },
            {
              key: 'relations',
              label: 'CRM 关联',
              children:
                record.crmRelations?.map((row) => (
                  <Tag key={`${row.objectType}:${row.objectUuid}`}>
                    {row.displayName || row.objectUuid}
                  </Tag>
                )) || '由出差助手校验并保存',
            },
          ]}
        />
      ) : null,
    },
    ...(plan
      ? [
          {
            key: 'itinerary',
            label: '行程编排',
            children: (
              <>
                {activityTable(record?.items || [])}
                {record?.days?.map((day) => (
                  <div key={day.executionDate}>
                    <Typography.Title level={5}>
                      {day.executionDate} · 每日备注
                    </Typography.Title>
                    <Input.TextArea
                      value={notes[day.executionDate]}
                      onChange={(event) =>
                        setNotes((value) => ({
                          ...value,
                          [day.executionDate]: event.target.value,
                        }))
                      }
                    />
                    <Button
                      disabled={!canEdit}
                      onClick={() =>
                        void mutate(
                          `/day-notes/${day.executionDate}`,
                          'PATCH',
                          { dailyNote: notes[day.executionDate] || '' },
                        )
                      }
                    >
                      保存备注
                    </Button>
                  </div>
                ))}
              </>
            ),
          },
          {
            key: 'candidates',
            label: '纳入事项',
            children: (
              <>
                <Table<TripRecord>
                  rowKey="uuid"
                  dataSource={candidates}
                  rowSelection={
                    canEdit
                      ? {
                          selectedRowKeys: picked,
                          onChange: (keys) => setPicked(keys.map(String)),
                        }
                      : undefined
                  }
                  columns={[
                    { title: '事项', dataIndex: 'title' },
                    { title: '城市', dataIndex: 'destinationCity' },
                  ]}
                />
                <Button
                  disabled={!canEdit || !picked.length}
                  loading={busy}
                  onClick={async () => {
                    if (
                      await mutate('/items', 'POST', {
                        businessTripItemUuids: picked,
                      })
                    ) {
                      setPicked([]);
                      void loadTab('candidates');
                    }
                  }}
                >
                  纳入计划
                </Button>
              </>
            ),
          },
          {
            key: 'recycle',
            label: '回收站',
            children: (
              <>
                <Space>
                  <Popconfirm
                    title="清理过期回收记录？此操作不可恢复。"
                    onConfirm={async () => {
                      if (await mutate('/recycle-bin/expired', 'DELETE'))
                        void loadTab('recycle');
                    }}
                  >
                    <Button danger disabled={!canDelete}>
                      清理过期记录
                    </Button>
                  </Popconfirm>
                  <Popconfirm
                    title="清空回收站？此操作不可恢复。"
                    onConfirm={async () => {
                      if (await mutate('/recycle-bin', 'DELETE'))
                        void loadTab('recycle');
                    }}
                  >
                    <Button danger disabled={!canDelete}>
                      清空回收站
                    </Button>
                  </Popconfirm>
                </Space>
                {activityTable(recycled, true)}
              </>
            ),
          },
        ]
      : []),
    {
      key: 'members',
      label: '协作成员',
      children: (
        <MemberManager
          path={path}
          plan={plan}
          members={members}
          canManage={canManage}
          onChanged={() => {
            void load();
            void loadTab('members');
          }}
        />
      ),
    },
    ...(kind !== 'activities'
      ? [
          {
            key: 'logs',
            label: '操作记录',
            children: (
              <>
                <CrmList
                  dataSource={logs}
                  renderItem={(row) => (
                    <CrmList.Item>
                      {row.createdAt} · {row.operatorName} ·{' '}
                      {row.summary || row.actionName || row.action}
                    </CrmList.Item>
                  )}
                />
                {logCursor && (
                  <Button onClick={() => void loadTab('logs', logCursor)}>
                    加载更多
                  </Button>
                )}
              </>
            ),
          },
        ]
      : []),
  ];
  return (
    <BusinessModal
      widthPreset="detail"
      rootClassName="crm-business-trip-surface"
      open
      title={record?.title || '出差详情'}
      onCancel={onClose}
      destroyOnHidden
      extra={
        <Space>
          <Button
            onClick={() => {
              void load();
              void loadTab(tab);
            }}
          >
            刷新
          </Button>
          {canEdit && kind !== 'activities' && (
            <Button onClick={() => setEditing(true)}>编辑</Button>
          )}
          {canEdit && kind === 'activities' && activity && (
            <Button onClick={() => setScheduling(activity)}>编辑排期</Button>
          )}
          {plan &&
            canEdit &&
            tripAllowed(access, 'complete') &&
            record?.status !== 'completed' && (
              <Button
                onClick={() =>
                  setCompletion(
                    Object.fromEntries(
                      (record?.items || [])
                        .filter((row) => row.executionStatus !== 'completed')
                        .map((row) => [row.businessTripItemUuid, 'completed']),
                    ),
                  )
                }
              >
                完成计划
              </Button>
            )}
          {canDelete && kind !== 'activities' && (
            <Popconfirm
              title={`删除此出差${plan ? '计划及其行程' : '事项及其关联'}？`}
              onConfirm={async () => {
                if (await mutate('', 'DELETE')) onClose();
              }}
            >
              <Button danger>删除</Button>
            </Popconfirm>
          )}
        </Space>
      }
    >
      {error && (
        <Alert
          type="error"
          showIcon
          title={error}
          action={<Button onClick={() => void load()}>重试</Button>}
        />
      )}
      <Spin spinning={loading || busy}>
        {record ? (
          <>
            {!plan && kind === 'items' && canEdit && (
              <Space>
                <Typography.Text>事项状态</Typography.Text>
                <Select
                  value={record.status}
                  options={options([
                    'planning',
                    'included',
                    'completed',
                    'canceled',
                    'assessable',
                    'deferred',
                  ])}
                  onChange={(status) =>
                    void mutate('/status', 'PATCH', { status })
                  }
                />
              </Space>
            )}
            <Tabs
              activeKey={tab}
              onChange={(key) => void loadTab(key)}
              items={tabs}
            />
          </>
        ) : (
          !loading && <Empty description={false} />
        )}
      </Spin>
      {editing && record && kind !== 'activities' && (
        <TripEditor
          kind={kind}
          initial={record}
          onClose={() => setEditing(false)}
          onSaved={() => void load()}
        />
      )}
      {scheduling && (
        <ScheduleEditor
          activity={scheduling}
          planUuid={scheduling.planUuid || uuid}
          onClose={() => setScheduling(undefined)}
          onSaved={() => void load()}
        />
      )}
      <Modal
        closable={{ 'aria-label': '关闭' }}
        keyboard
        rootClassName="crm-business-trip-surface"
        open={Boolean(completion)}
        title="完成计划：逐项确认未结束事项的处理方式"
        onCancel={() => setCompletion(undefined)}
        confirmLoading={busy}
        onOk={async () => {
          if (
            await mutate('/complete', 'POST', {
              itemActions: Object.entries(completion || {}).map(
                ([businessTripItemUuid, action]) => ({
                  businessTripItemUuid,
                  action,
                }),
              ),
            })
          )
            setCompletion(undefined);
        }}
      >
        {Object.entries(completion || {}).map(([itemUuid, action]) => (
          <div key={itemUuid}>
            <Typography.Paragraph>
              {record?.items?.find(
                (row) => row.businessTripItemUuid === itemUuid,
              )?.item?.title || itemUuid}
            </Typography.Paragraph>
            <Select
              value={action}
              style={{ width: '100%' }}
              options={[
                { value: 'completed', label: '完成' },
                { value: 'removed', label: '移出计划' },
                { value: 'canceled', label: '取消' },
                { value: 'deleted', label: '删除' },
              ]}
              onChange={(value) =>
                setCompletion((previous) => ({
                  ...previous,
                  [itemUuid]: value,
                }))
              }
            />
          </div>
        ))}
      </Modal>
      <Modal
        closable={{ 'aria-label': '关闭' }}
        keyboard
        rootClassName="crm-business-trip-surface"
        open={Boolean(removing)}
        title="移出事项"
        onCancel={() => setRemoving(undefined)}
        onOk={async () => {
          if (
            removing &&
            (await mutate(
              `/items/${id(removing.businessTripItemUuid)}/remove`,
              'PATCH',
              { reasonTemplate: reason, reasonDetail },
            ))
          )
            setRemoving(undefined);
        }}
        confirmLoading={busy}
      >
        <Select
          value={reason}
          onChange={setReason}
          style={{ width: '100%' }}
          options={[
            { value: 'customer_unavailable', label: '客户不方便' },
            { value: 'customer_busy', label: '客户忙碌' },
            { value: 'customer_traveling', label: '客户出差' },
            { value: 'itinerary_changed', label: '行程变更' },
            { value: 'other', label: '其他' },
          ]}
        />
        <Input.TextArea
          value={reasonDetail}
          onChange={(event) => setReasonDetail(event.target.value)}
          maxLength={500}
          placeholder="移出原因说明"
        />
      </Modal>
    </BusinessModal>
  );
}
